/**
 * Autonomous Agent Demo Page
 * 
 * Demonstrates all 12 critical components of the autonomous email/calendar agent:
 * 1. Self-Learning Memory
 * 2. Context Awareness
 * 3. Predictive Intelligence
 * 4. Adaptive Learning
 * 5. Multi-Modal Input
 * 6. Intelligent Routing
 * 7. Self-Scaling
 * 8. Natural Interaction
 * 9. Security
 * 10. Reliability
 * 11. Performance
 * 12. Integration
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Brain, 
  Sparkles, 
  Zap, 
  Target, 
  CheckCircle2, 
  AlertCircle,
  TrendingUp,
  Clock,
  Users,
  Calendar,
  Mail,
  Shield,
  Activity,
  Layers
} from 'lucide-react';
import { autonomousAgent } from '@/services/autonomousAgent';
import { blink } from '@/lib/blink';
import Markdown from '@/components/Markdown';

export default function AutonomousAgentDemo() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Agent state
  const [userMessage, setUserMessage] = useState('');
  const [agentResponse, setAgentResponse] = useState<any>(null);
  const [capabilities, setCapabilities] = useState<any>(null);
  const [dailyBriefing, setDailyBriefing] = useState('');
  const [weeklyReflection, setWeeklyReflection] = useState<any>(null);
  
  // Demo scenarios
  const scenarios = [
    {
      title: "Schedule Meeting with Context",
      message: "I need to meet with Sarah about the Q4 budget review",
      icon: Calendar,
      color: "blue"
    },
    {
      title: "Smart Email Reply",
      message: "Draft a response to the client inquiry about project timeline",
      icon: Mail,
      color: "green"
    },
    {
      title: "Conflict Detection",
      message: "Check my schedule for any conflicts or overcommitments this week",
      icon: AlertCircle,
      color: "orange"
    },
    {
      title: "Proactive Optimization",
      message: "What opportunities do you see to optimize my calendar?",
      icon: TrendingUp,
      color: "purple"
    }
  ];

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user);
      if (state.user && !initialized) {
        initializeAgent(state.user.id);
      }
    });
    return unsubscribe;
  }, [initialized]);

  const initializeAgent = async (userId: string) => {
    try {
      setLoading(true);
      await autonomousAgent.initialize(userId);
      const caps = autonomousAgent.getCapabilities();
      setCapabilities(caps);
      setInitialized(true);
      
      // Generate daily briefing
      const briefing = await autonomousAgent.generateDailyBriefing();
      setDailyBriefing(briefing);
    } catch (error) {
      console.error('Failed to initialize agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const processRequest = async () => {
    if (!userMessage.trim() || !user) return;
    
    try {
      setLoading(true);
      const result = await autonomousAgent.processRequest(userMessage);
      setAgentResponse(result);
    } catch (error) {
      console.error('Failed to process request:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScenario = (scenario: any) => {
    setUserMessage(scenario.message);
  };

  const performWeeklyReflection = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const reflection = await autonomousAgent.performWeeklyReflection();
      setWeeklyReflection(reflection);
    } catch (error) {
      console.error('Failed to perform reflection:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to access the Autonomous Agent Demo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => blink.auth.login()} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">
          🤖 Autonomous Email & Calendar Agent
        </h1>
        <p className="text-muted-foreground text-lg">
          Experience human-level email and calendar management with all 12 critical components integrated
        </p>
      </div>

      {/* Capabilities Overview */}
      {capabilities && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Agent Capabilities
            </CardTitle>
            <CardDescription>
              All 12 critical components are active and operational
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(capabilities).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  {value ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm">
                    {key.replace(/^can/, '').replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="interact" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="interact">
            <Zap className="h-4 w-4 mr-2" />
            Interact
          </TabsTrigger>
          <TabsTrigger value="briefing">
            <Clock className="h-4 w-4 mr-2" />
            Daily Briefing
          </TabsTrigger>
          <TabsTrigger value="reflection">
            <TrendingUp className="h-4 w-4 mr-2" />
            Weekly Reflection
          </TabsTrigger>
          <TabsTrigger value="architecture">
            <Layers className="h-4 w-4 mr-2" />
            Architecture
          </TabsTrigger>
        </TabsList>

        {/* Interact Tab */}
        <TabsContent value="interact" className="space-y-6">
          {/* Quick Scenarios */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Scenarios</CardTitle>
              <CardDescription>
                Try these example scenarios to see the autonomous agent in action
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {scenarios.map((scenario, index) => {
                  const Icon = scenario.icon;
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-auto p-4 justify-start"
                      onClick={() => loadScenario(scenario)}
                    >
                      <Icon className={`h-5 w-5 mr-3 text-${scenario.color}-500`} />
                      <div className="text-left">
                        <div className="font-semibold">{scenario.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {scenario.message}
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Interaction Area */}
          <Card>
            <CardHeader>
              <CardTitle>Chat with Autonomous Agent</CardTitle>
              <CardDescription>
                The agent uses all 12 components to understand context, predict needs, and act autonomously
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                placeholder="Type your request here... (e.g., 'Schedule a meeting with Sarah about budget')"
                rows={3}
              />
              <Button 
                onClick={processRequest} 
                disabled={loading || !userMessage.trim()}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Activity className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Process Request
                  </>
                )}
              </Button>

              {agentResponse && (
                <div className="space-y-4 mt-6">
                  <Separator />
                  
                  {/* Response */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      Agent Response
                    </h3>
                    <div className="bg-muted p-4 rounded-lg">
                      <Markdown content={agentResponse.response} />
                    </div>
                  </div>

                  {/* Actions Taken */}
                  {agentResponse.actions.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Actions Taken
                      </h3>
                      <div className="space-y-2">
                        {agentResponse.actions.map((action: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div>
                              <div className="font-medium">{action.type.replace(/_/g, ' ')}</div>
                              {action.prediction && (
                                <div className="text-sm text-muted-foreground">
                                  Predicted: {action.prediction}
                                </div>
                              )}
                            </div>
                            <Badge variant={
                              action.status === 'executed' ? 'default' : 
                              action.status === 'pending_approval' ? 'secondary' : 
                              'outline'
                            }>
                              {action.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Routing Decisions */}
                  {agentResponse.routingDecisions.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Intelligent Routing
                      </h3>
                      <div className="space-y-2">
                        {agentResponse.routingDecisions.map((decision: any, index: number) => (
                          <div key={index} className="p-3 bg-muted rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <Badge>{decision.route.replace(/_/g, ' ')}</Badge>
                              <div className="text-sm">
                                Confidence: {(decision.confidence * 100).toFixed(0)}% | 
                                Risk: {(decision.riskScore * 100).toFixed(0)}%
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {decision.reasoning}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Predictions */}
                  {agentResponse.predictions.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        Predictive Intelligence
                      </h3>
                      <div className="space-y-2">
                        {agentResponse.predictions.slice(0, 3).map((pred: any, index: number) => (
                          <div key={index} className="p-3 bg-muted rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <div className="font-medium">{pred.action}</div>
                              <Badge variant="outline">
                                {(pred.confidence * 100).toFixed(0)}% confidence
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground mb-2">
                              {pred.reasoning}
                            </div>
                            <Progress value={pred.confidence * 100} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Context Used */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Context Awareness
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">Temporal Context</div>
                        <div className="text-sm text-muted-foreground">
                          Time: {new Date().toLocaleTimeString()}
                          <br />
                          {agentResponse.contextUsed.temporal?.shouldDefer && 
                            `⚠️ Deferred until ${agentResponse.contextUsed.temporal.deferUntil}`
                          }
                        </div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">Behavioral Context</div>
                        <div className="text-sm text-muted-foreground">
                          Workload: {agentResponse.contextUsed.behavioral?.currentState?.workload || 'N/A'}
                          <br />
                          Stress: {agentResponse.contextUsed.behavioral?.currentState?.stressLevel || 'Normal'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Learnings */}
                  {agentResponse.learnings.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Adaptive Learning
                      </h3>
                      <div className="space-y-2">
                        {agentResponse.learnings.map((learning: string, index: number) => (
                          <div key={index} className="p-2 bg-muted rounded text-sm">
                            {learning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Briefing Tab */}
        <TabsContent value="briefing">
          <Card>
            <CardHeader>
              <CardTitle>Daily Proactive Briefing</CardTitle>
              <CardDescription>
                Generated automatically every morning with predictions and optimization opportunities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                {dailyBriefing ? (
                  <Markdown content={dailyBriefing} />
                ) : (
                  <div className="text-center text-muted-foreground py-12">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No briefing available yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Reflection Tab */}
        <TabsContent value="reflection">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Self-Reflection</CardTitle>
              <CardDescription>
                The agent analyzes its own performance and identifies areas for improvement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={performWeeklyReflection} disabled={loading}>
                {loading ? 'Analyzing...' : 'Perform Weekly Reflection'}
              </Button>

              {weeklyReflection && (
                <div className="space-y-4">
                  {/* Confidence Score */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Overall Confidence</span>
                      <span className="text-lg font-bold">
                        {(weeklyReflection.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                    <Progress value={weeklyReflection.confidence * 100} className="h-3" />
                  </div>

                  <Separator />

                  {/* Successes */}
                  <div>
                    <h3 className="font-semibold mb-2 text-green-600 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      What Worked Well
                    </h3>
                    <ul className="space-y-2">
                      {weeklyReflection.successes.map((success: string, index: number) => (
                        <li key={index} className="p-2 bg-green-50 dark:bg-green-950 rounded text-sm">
                          {success}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Failures */}
                  <div>
                    <h3 className="font-semibold mb-2 text-orange-600 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      What Didn't Work
                    </h3>
                    <ul className="space-y-2">
                      {weeklyReflection.failures.map((failure: string, index: number) => (
                        <li key={index} className="p-2 bg-orange-50 dark:bg-orange-950 rounded text-sm">
                          {failure}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Improvements */}
                  <div>
                    <h3 className="font-semibold mb-2 text-blue-600 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Planned Improvements
                    </h3>
                    <ul className="space-y-2">
                      {weeklyReflection.improvements.map((improvement: string, index: number) => (
                        <li key={index} className="p-2 bg-blue-50 dark:bg-blue-950 rounded text-sm">
                          {improvement}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Architecture Tab */}
        <TabsContent value="architecture">
          <Card>
            <CardHeader>
              <CardTitle>12 Critical Components Architecture</CardTitle>
              <CardDescription>
                Complete breakdown of the autonomous agent's capabilities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { num: 1, name: 'Self-Learning Memory', icon: Brain, desc: '4-layer memory: Episodic, Semantic, Procedural, Working' },
                  { num: 2, name: 'Context Awareness Engine', icon: Users, desc: 'Temporal, Relational, Project, Behavioral, Environmental' },
                  { num: 3, name: 'Predictive Intelligence', icon: Sparkles, desc: 'Intention prediction, conflict detection, opportunity ID' },
                  { num: 4, name: 'Adaptive Learning', icon: TrendingUp, desc: 'Explicit & implicit feedback, self-reflection' },
                  { num: 5, name: 'Multi-Modal Input', icon: Layers, desc: 'Email, calendar, docs, chat, CRM, voice, external data' },
                  { num: 6, name: 'Intelligent Routing', icon: Target, desc: 'Confidence-based routing with human-in-the-loop' },
                  { num: 7, name: 'Self-Scaling Architecture', icon: Activity, desc: 'Dynamic agent provisioning, load prediction' },
                  { num: 8, name: 'Natural Interaction', icon: Zap, desc: 'Proactive briefings, conversational UI, transparent reasoning' },
                  { num: 9, name: 'Security & Privacy', icon: Shield, desc: 'Per-user isolation, encryption, audit trails' },
                  { num: 10, name: 'Reliability', icon: CheckCircle2, desc: 'Circuit breakers, retry logic, graceful degradation' },
                  { num: 11, name: 'Performance', icon: Clock, desc: 'Caching, batching, model optimization' },
                  { num: 12, name: 'Integration Ecosystem', icon: Mail, desc: 'Graph API, Blink SDK, webhooks, OAuth 2.0' }
                ].map((component) => {
                  const Icon = component.icon;
                  return (
                    <div key={component.num} className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                          {component.num}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4" />
                          <h4 className="font-semibold">{component.name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">{component.desc}</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
